import { defineStore } from 'pinia'
import router from '@/router'
import type { UserDetails, UserRole, UserTier } from '@/types/user.types'
import { signIn, signUp, signOut, getCurrentUser, fetchUserAttributes, resendSignUpCode, confirmSignUp, resetPassword, confirmResetPassword, fetchAuthSession, type SignUpOutput, type ResetPasswordOutput } from 'aws-amplify/auth'
import { splitAuthName } from '@/utils/StringUtils'
import { clearContextCache } from '@/utils/clearContextCache'
import { profileService, type Membership } from '@/services/profile'

// Amplify v6 signIn throws UserAlreadyAuthenticatedException when a residual
// Cognito session is still cached in storage. This bites "especially during
// updates" (IRL2-2896): a deploy changes the token/claim shape, checkAuth
// silently bails and the UI shows the login modal, but Amplify still holds the
// old tokens — so the next signIn refuses with "There is already a signed in
// user." and the user is stuck. Clear the stale session and retry once so the
// credentials just entered win, instead of a dead-end error.
const USER_ALREADY_AUTHENTICATED = 'UserAlreadyAuthenticatedException'

async function signInRecoveringStaleSession(username: string, password: string) {
    try {
        return await signIn({ username, password })
    } catch (e: unknown) {
        if ((e as Error)?.name !== USER_ALREADY_AUTHENTICATED) throw e
        console.warn('Stale Cognito session on login; signing out and retrying.')
        clearContextCache()
        await signOut()
        return await signIn({ username, password })
    }
}

type AuthState = {
  isAuthenticated: boolean
  isEmailVerified: boolean
  userId: string | null
  userDetails: UserDetails
  userRole: UserRole
  userTier: UserTier
  features: string[]
  planId: string
  limits: Record<string, number>
  activeOrgId: string | null
  activeTenantId: string | null
  activeOrgRole: string | null
  activeTenantRole: string | null
  memberships: Membership[]
  isLoadingMemberships: boolean
  isSwitchingContext: boolean
  isLoading: boolean
  error: string | null
}

export interface RegisterResult {
  isSignUpComplete: boolean
  userId?: string
  nextStep?: SignUpOutput['nextStep']
  usernameUsed: string
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    isAuthenticated: false,
    isEmailVerified: false,
    userId: null,
    userDetails: { name: '', email: '' },
    userRole: 'freemium',
    userTier: 'freemium',
    features: [],
    planId: 'free',
    limits: {},
    activeOrgId: null,
    activeTenantId: null,
    activeOrgRole: null,
    activeTenantRole: null,
    memberships: [],
    isLoadingMemberships: false,
    isSwitchingContext: false,
    isLoading: false,
    error: null,
  }),
  getters: {
    activeOrgName(state): string | null {
      if (!state.activeOrgId) return null
      const stripped = state.activeOrgId.replace(/^[A-Z]+#/, '')
      const match = state.memberships.find(
        (m) => m.kind === 'org' && (m.entityId === state.activeOrgId || m.entityId.endsWith(stripped))
      )
      return match?.name || null
    },
    activeTenantName(state): string | null {
      if (!state.activeTenantId) return null
      const stripped = state.activeTenantId.replace(/^[A-Z]+#/, '')
      const match = state.memberships.find(
        (m) => m.kind === 'tenant' && (m.entityId === state.activeTenantId || m.entityId.endsWith(stripped))
      )
      return match?.name || null
    },
  },
  actions: {
    async checkAuth() {
        let hadSession = false;
        try {
            const { userId } = await getCurrentUser();
            hadSession = true;
            this.isAuthenticated = true;
            this.userId = userId;
            const attributes = await fetchUserAttributes();
            const emailVerified = String(attributes.email_verified) === 'true';

            // Split name if given_name is missing but preferred_username exists
            let name = attributes.given_name || '';
            let surname = attributes.family_name || '';

            if (!name && attributes.preferred_username) {
              const split = splitAuthName(attributes.preferred_username);
              name = split.firstName;
              surname = split.surname;
            } else if (!name && attributes.email) {
              const emailPrefix = attributes.email.split('@')[0];
              const split = splitAuthName(emailPrefix);
              name = split.firstName;
              surname = split.surname;
            }

            this.userDetails = {
                name,
                surname,
                email: attributes.email || '',
                phoneNumber: attributes.phone_number || '',
            }
            this.isEmailVerified = emailVerified;

            await this.loadEntitlements();
            // Memberships carry org/tenant names — needed by HeaderComp profile
            // menu (and anywhere else that wants the human-readable label for
            // the active context). Fire-and-forget; failure is non-fatal.
            void this.loadMemberships();
        } catch {
            this.isAuthenticated = false;
            this.isEmailVerified = false;
            this.userId = null;
            this.userDetails = { name: '', email: '' };
            // If getCurrentUser succeeded but bootstrap failed afterwards, the
            // tokens parsed yet the session is unusable (the "during updates"
            // case in IRL2-2896). Leaving them cached makes the next signIn
            // throw UserAlreadyAuthenticatedException. Clear them best-effort so
            // the login modal that follows can actually sign in. (A bare
            // logged-out state — getCurrentUser itself threw — needs no signOut.)
            if (hadSession) {
                try {
                    clearContextCache();
                    await signOut();
                } catch (signOutError) {
                    console.warn('checkAuth: failed to clear unusable session', signOutError);
                }
            }
        }
    },

    async loadEntitlements() {
        try {
            const session = await fetchAuthSession();
            const claims = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
            if (!claims) return;

            const featuresRaw = claims['custom:context_features'] as string | undefined;
            const planRaw = claims['custom:context_plan'] as string | undefined;
            const limitsRaw = claims['custom:context_limits'] as string | undefined;

            this.features = featuresRaw ? JSON.parse(featuresRaw) : [];
            this.planId = planRaw || 'free';
            this.limits = limitsRaw ? JSON.parse(limitsRaw) : {};

            this.activeOrgId = (claims['custom:context_org_id'] as string) || null;
            this.activeTenantId = (claims['custom:context_tenant_id'] as string) || null;
            this.activeOrgRole = (claims['custom:context_org_role'] as string) || null;
            this.activeTenantRole = (claims['custom:context_tenant_role'] as string) || null;

            if (this.planId !== 'free' && this.features.length > 0) {
                if (this.features.includes('white_label') || this.features.includes('sso_login')) {
                    this.userTier = 'enterprise';
                    this.userRole = 'enterprise';
                } else if (this.features.includes('analytics.advanced')) {
                    this.userTier = 'premium';
                    this.userRole = 'premium';
                } else {
                    this.userTier = 'freemium';
                    this.userRole = 'freemium';
                }
            }
        } catch (e) {
            console.warn('Failed to load entitlements from token', e);
        }
    },

    async login(username: string, password: string) {
        this.isLoading = true;
        this.error = null;
        clearContextCache();
        console.log('🔑 Authenticating...', { username: JSON.stringify(username) })
        try {
            const { isSignedIn } = await signInRecoveringStaleSession(username, password);
            if (isSignedIn) {
                await this.checkAuth();
            }
        } catch (e: unknown) {
            const err = e as Error
            // Don't log expected auth errors as errors to keep console clean
            if (err.name === 'NotAuthorizedException' || err.name === 'UserNotConfirmedException') {
              console.warn('Login attempt failed:', err.name, err.message);
            } else {
              console.error('Login error', err);
            }
            this.error = err.message;
            throw err;
        } finally {
            this.isLoading = false;
        }
    },

    async register(email: string, password: string, username?: string, phoneNumber?: string): Promise<RegisterResult> {
        this.isLoading = true;
        this.error = null;
        try {
             const cognitoUsername = email;
             
             const userAttributes: Record<string, string> = {
                email
             };

             if (phoneNumber) {
                userAttributes.phone_number = phoneNumber;
             }
             if (username) {
                userAttributes.preferred_username = username;
             }
             
             const { isSignUpComplete, userId, nextStep } = await signUp({
                username: cognitoUsername,
                password,
                options: {
                    userAttributes
                }
            });
            return { isSignUpComplete, userId, nextStep, usernameUsed: cognitoUsername };
        } catch (e: unknown) {
            const err = e as Error
            // Don't log expected registration errors as errors
            if (err.name === 'UsernameExistsException') {
               console.warn('Registration attempt with existing username:', email);
            } else {
               console.error('Register error', err);
            }
            this.error = err.message;
            throw err;
        } finally {
            this.isLoading = false;
        }
    },

    async confirmSignUp(username: string, code: string) {
        this.isLoading = true;
        this.error = null;
        try {
            await confirmSignUp({ username, confirmationCode: code });
            this.isEmailVerified = true;
        } catch (error: unknown) {
            const err = error as Error;
            console.error('Confirmation error', err);
            this.error = err.message;
            throw err;
        } finally {
            this.isLoading = false;
        }
    },

    async resendVerification(username?: string) {
        try {
            // If username is provided, use it (for unauthenticated flow)
            // Otherwise try to get current user (for authenticated flow)
            if (username) {
                await resendSignUpCode({ username });
            } else {
                const current = await getCurrentUser();
                await resendSignUpCode({ username: current.username });
            }
        } catch (e) {
            console.error('Resend verification failed', e);
            throw e;
        }
    },

    async logout() {
        try {
            clearContextCache();
            await signOut();
            this.isAuthenticated = false;
            this.isEmailVerified = false;
            this.userId = null;
            this.userDetails = { name: '', email: '' };
            this.userRole = 'freemium';
            this.userTier = 'freemium';
            this.features = [];
            this.planId = 'free';
            this.limits = {};
            this.activeOrgId = null;
            this.activeTenantId = null;
            this.activeOrgRole = null;
            this.activeTenantRole = null;
            this.memberships = [];
            clearContextCache();
            router.push('/');
        } catch (e) {
            console.error('Logout error', e);
        }
    },

    async loadMemberships() {
        if (!this.isAuthenticated) return;
        this.isLoadingMemberships = true;
        try {
            this.memberships = await profileService.getMemberships();
        } catch (e) {
            console.error('Failed to load memberships', e);
        } finally {
            this.isLoadingMemberships = false;
        }
    },

    async switchActiveContext(payload: { orgId?: string; tenantId?: string }) {
        if (!this.isAuthenticated) return;
        if (!payload.orgId && !payload.tenantId) return;
        this.isSwitchingContext = true;
        try {
            await profileService.setActiveContext(payload);
            clearContextCache();

            // First refresh attempt
            let session = await fetchAuthSession({ forceRefresh: true });

            // Verify the new token actually carries the requested context claim.
            // Claims are clean UUIDs (PreTokenGen strips ORG#/TENANT# prefixes);
            // the request payload may also arrive as a clean UUID — strip just in case.
            const cleanId = (id: string) => id.replace(/^[A-Z]+#/, '');
            const claims = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
            const claimedOrg = claims?.['custom:context_org_id'] as string | undefined;
            const claimedTenant = claims?.['custom:context_tenant_id'] as string | undefined;

            const orgMatch = !payload.orgId || claimedOrg === cleanId(payload.orgId);
            const tenantMatch = !payload.tenantId || claimedTenant === cleanId(payload.tenantId);

            if (!orgMatch || !tenantMatch) {
                // One retry after a short back-off — Cognito may need a moment to propagate
                await new Promise((r) => setTimeout(r, 600));
                session = await fetchAuthSession({ forceRefresh: true });
                const retryClaims = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
                const retryOrg = retryClaims?.['custom:context_org_id'] as string | undefined;
                const retryTenant = retryClaims?.['custom:context_tenant_id'] as string | undefined;

                const retryOrgMatch = !payload.orgId || retryOrg === cleanId(payload.orgId);
                const retryTenantMatch = !payload.tenantId || retryTenant === cleanId(payload.tenantId);

                if (!retryOrgMatch || !retryTenantMatch) {
                    throw new Error('Token refresh did not pick up new context — please sign out and back in');
                }
            }

            // Hard-navigate so every mounted component re-bootstraps with the new token.
            // checkAuth re-runs via main.ts:18 / router beforeEach (router/index.ts:108).
            window.location.assign('/dashboard');
        } catch (e) {
            console.error('Failed to switch active context', e);
            throw e;
        } finally {
            // Clears the spinner on error path so the user can retry.
            this.isSwitchingContext = false;
        }
    },
    async requestPasswordReset(username: string): Promise<ResetPasswordOutput> {
        this.isLoading = true;
        this.error = null;
        try {
            const result = await resetPassword({ username });
            return result;
        } catch (e: unknown) {
            const err = e as Error;
            console.error('Password reset request failed', err);
            this.error = err.message;
            throw err;
        } finally {
            this.isLoading = false;
        }
    },
    async confirmPasswordReset(username: string, code: string, newPassword: string) {
        this.isLoading = true;
        this.error = null;
        try {
            await confirmResetPassword({
                username,
                confirmationCode: code,
                newPassword
            });
        } catch (e: unknown) {
            const err = e as Error;
            console.error('Password reset confirmation failed', err);
            this.error = err.message;
            throw err;
        } finally {
            this.isLoading = false;
        }
    }
  },
})
